import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, Inbox, Loader2, Volume2, VolumeX, Settings, ExternalLink } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { toast } from 'react-hot-toast';
import { useCurrentUser } from '../contexts/AuthContext';
import { 
  getUnreadNotificationCount, 
  getNotifications, 
  markAllNotificationsAsRead,
  markNotificationAsRead 
} from '../api/worker';
import { SHOULD_USE_MOCKS } from '../lib/clerk';

// Ensure this interface matches the OpenAPI Notification schema
interface Notification {
  id: string;
  userId: string;
  nodeId: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  linkUrl?: string;
  createdAt: string;
  read: boolean;
}

// Pre-load the audio file so it is ready to play instantly.
// (Make sure to place a soft sound file in your public/sounds directory!)
const chimeSound = typeof Audio !== 'undefined' ? new Audio('/sounds/chime.mp3') : null;

interface NotificationBellProps {
  className?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  
  // Auth hook from context for mock compatibility
  const { getToken, isSignedIn } = useCurrentUser();
  const errorCount = useRef(0);
  
  // State for mute toggle, persisting in localStorage
  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('notifications_muted') === 'true';
    }
    return false;
  });

  const isMutedRef = useRef(isMuted);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  // 1. Fetch unread count (initially, then updated by SSE)
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: getUnreadNotificationCount,
    enabled: isSignedIn || SHOULD_USE_MOCKS
  });

  // 2. Fetch the notification list (only fetched when the user opens the dropdown or every 5 mins)
  const { data: notificationsPage, isLoading } = useQuery({
    queryKey: ['notifications-list'],
    queryFn: () => getNotifications(0, 20),
    enabled: isOpen && (isSignedIn || SHOULD_USE_MOCKS), // Saves unnecessary requests until the bell is actually clicked
    staleTime: 5 * 60 * 1000, 
  });

  // 3. Mutation: Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    }
  });

  // 4. Mutation: Mark single notification as read
  const markSingleReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    }
  });

  // Handle click outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Real-time broadcast support for mock mode
  useEffect(() => {
    const handleMockBroadcast = (e: Event) => {
      const customEvent = e as CustomEvent;
      const payload = customEvent.detail;
      if (!payload) return;

      const typeUpper = (payload?.type || '').toUpperCase();
      const isUrgent = typeUpper === 'URGENT' || typeUpper === 'EMERGENCY' || typeUpper === 'ALERT' || payload?.severity === 'CRITICAL';
      const toastId = `notif-${payload.id || Date.now()}`;

      if (isUrgent) {
        toast.error(
          <div 
            className={payload.link ? 'cursor-pointer' : ''}
            onClick={() => {
              if (payload.link) {
                toast.dismiss(toastId);
                navigate(payload.link);
              }
            }}
          >
            <div className="font-black uppercase tracking-wider">{payload.title || 'Urgent Alert'}</div>
            {payload.message && (
              <div className="text-sm font-normal mt-0.5">{payload.message}</div>
            )}
            {payload.link && (
              <div className="text-xs text-red-600 mt-1 font-semibold hover:underline">
                Click to view details &rarr;
              </div>
            )}
          </div>, 
          {
            id: toastId,
            duration: 8000,
            icon: '⚠️',
            style: {
              fontWeight: 'bold',
              border: '2px solid #EF4444'
            }
          }
        );
      } else {
        toast(
          <div 
            className={payload.link ? 'cursor-pointer' : ''}
            onClick={() => {
              if (payload.link) {
                toast.dismiss(toastId);
                navigate(payload.link);
              }
            }}
          >
            <div className="font-black uppercase tracking-wider text-gray-900 dark:text-white">
              {payload.title || 'Announcement'}
            </div>
            {payload.message && (
              <div className="text-sm font-normal text-gray-700 dark:text-gray-300 mt-0.5">{payload.message}</div>
            )}
            {payload.link && (
              <div className="text-xs text-[#0044FF] mt-1 font-semibold hover:underline">
                Click to view details &rarr;
              </div>
            )}
          </div>, 
          {
            id: toastId,
            duration: 6000,
            icon: '📢',
            style: {
              fontWeight: 'bold',
              border: '2px solid #000000',
              borderRadius: '0px',
              boxShadow: '4px 4px 0px #000000'
            }
          }
        );
      }

      if (chimeSound && !isMutedRef.current) {
        chimeSound.volume = 0.4;
        chimeSound.currentTime = 0;
        chimeSound.play().catch(err => console.debug('[Mock Broadcast] Chime autoplay blocked', err));
      }

      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    };

    window.addEventListener('mock-notification-broadcast', handleMockBroadcast);
    return () => window.removeEventListener('mock-notification-broadcast', handleMockBroadcast);
  }, [queryClient]);

  // 5. Establish Server-Sent Events (SSE) connection for real-time updates
  useEffect(() => {
    // Skip SSE in mock mode or if not signed in or if we've had too many successive errors
    if (SHOULD_USE_MOCKS || !isSignedIn || errorCount.current > 5) return;

    const abortController = new AbortController();
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    let isMounted = true;

    const connectSSE = async () => {
      try {
        const token = await getToken();
        if (!isMounted) return;
        if (!token) {
          // Require authentication token before attempting stream connection
          return;
        }
        
        // Target backend API URL (matching apiWorker) or relative path
        const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
        
        // Context resolution for multi-tenancy
        const searchParams = new URLSearchParams(window.location.search);
        const orgParam = searchParams.get('org');
        const storedOrg = localStorage.getItem('active_org');
        const slug = orgParam || storedOrg || 'global';

        // We pass the token via Authorization header now using fetchEventSource
        const url = `${baseUrl}/api/notifications/stream?tenant=${slug}`;
        
        const { fetchEventSource } = await import('@microsoft/fetch-event-source');
        
        fetchEventSource(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: abortController.signal,
          onopen: async (res) => {
            if (res.ok && isMounted) {
              errorCount.current = 0;
            } else if (res.status === 401 || res.status === 403) {
              // Authentication failed: stop stream and prevent retry loop
              errorCount.current = 999;
              abortController.abort();
              throw new Error(`Authentication required for notification stream: ${res.status}`);
            } else if (res.status >= 400 && res.status < 500) {
              throw new Error(`Connection error: ${res.status}`);
            }
          },
          onmessage: (event) => {
          let isUrgent = false;
          let payload: any = null;
          
          try {
            // Parse the SSE payload string into a JSON object
            payload = JSON.parse(event.data);
            const typeUpper = payload?.type?.toUpperCase() || '';
            // Check if it matches urgent notification criteria
            isUrgent = typeUpper === 'URGENT' || 
                       typeUpper === 'EMERGENCY' || 
                       typeUpper === 'ALERT' || 
                       payload?.severity === 'CRITICAL';
          } catch (e) {
            // Gracefully ignore plain-text keep-alive pings
          }

          if (isUrgent && payload) {
            const toastId = `urgent-${payload.id || Date.now()}`;
            // Trigger the on-screen toast alert
            toast.error(
              <div 
                className={payload.link ? 'cursor-pointer' : ''}
                onClick={() => {
                  if (payload.link) {
                    toast.dismiss(toastId);
                    navigate(payload.link);
                  }
                }}
              >
                <div className="font-black uppercase tracking-wider">{payload.title || 'Urgent Alert'}</div>
                {payload.title && payload.message && (
                  <div className="text-sm font-normal mt-0.5">{payload.message}</div>
                )}
                {!payload.title && (
                  <div className="text-sm font-normal mt-0.5">{payload.message || 'Urgent Notification Received'}</div>
                )}
                {payload.link && (
                  <div className="text-xs text-red-600 mt-1 font-semibold hover:underline">
                    Click to view details &rarr;
                  </div>
                )}
              </div>, 
              {
                id: toastId, // Prevents duplicate spamming
                duration: 8000, // Keep it visible longer than a standard toast
                icon: '⚠️',
                style: {
                  fontWeight: 'bold',
                  border: '2px solid #EF4444' // Add a stark red border for high visibility
                }
              }
            );
          } else if (payload && (payload.broadcastId || ['ANNOUNCEMENT', 'BROADCAST', 'SYSTEM_ALERT'].includes((payload.type || '').toUpperCase()))) {
            const toastId = `bcast-${payload.id || Date.now()}`;
            toast(
              <div 
                className={payload.link ? 'cursor-pointer' : ''}
                onClick={() => {
                  if (payload.link) {
                    toast.dismiss(toastId);
                    navigate(payload.link);
                  }
                }}
              >
                <div className="font-black uppercase tracking-wider text-gray-900 dark:text-white">
                  {payload.title || 'Announcement'}
                </div>
                {payload.message && (
                  <div className="text-sm font-normal text-gray-700 dark:text-gray-300 mt-0.5">{payload.message}</div>
                )}
                {payload.link && (
                  <div className="text-xs text-[#0044FF] mt-1 font-semibold hover:underline">
                    Click to view details &rarr;
                  </div>
                )}
              </div>,
              {
                id: toastId,
                duration: 6000,
                icon: '📢',
                style: {
                  fontWeight: 'bold',
                  border: '2px solid #000000',
                  borderRadius: '0px',
                  boxShadow: '4px 4px 0px #000000'
                }
              }
            );
          }

          // Play the soft chime sound only if not muted AND the notification is urgent or broadcast
          const isBroadcastNotification = payload?.broadcastId || ['ANNOUNCEMENT', 'BROADCAST', 'SYSTEM_ALERT'].includes((payload?.type || '').toUpperCase());
          if (chimeSound && !isMutedRef.current && (isUrgent || isBroadcastNotification)) {
            chimeSound.volume = 0.4; // Keep it subtle and soft
            chimeSound.currentTime = 0; // Reset to start in case it's already playing
            // Catch autoplay restrictions to prevent unhandled promise rejections
            chimeSound.play().catch(e => console.debug('[SSE] Chime autoplay blocked by browser', e));
          }

          // When the server pushes an event, invalidate queries to fetch fresh data
          // ONLY IF IT HAS A VALID PAYLOAD AND NOT A KEEPALIVE PING
          if (payload) {
            queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
            queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
          }
          
          },
          onerror: (error) => {
            if (abortController.signal.aborted || errorCount.current > 5) {
              throw error;
            }
            console.warn('[SSE] Connection error', error);
            
            if (isMounted) {
              errorCount.current += 1;
            }
            if (errorCount.current > 5) {
              throw error; // Stop retrying if too many errors
            }
          },
          onclose: () => {
            // Stream closed
          }
        }).catch((error) => {
          if (abortController.signal.aborted || errorCount.current > 5) {
            return;
          }
          console.warn('[SSE] fetchEventSource stream failed or stopped.', error.message || error);
          if (isMounted && errorCount.current <= 5) {
            errorCount.current += 1;
            const delay = Math.min(1000 * Math.pow(2, errorCount.current), 30000);
            retryTimeout = setTimeout(connectSSE, delay);
          }
        });
      } catch (error) {
        console.warn('[SSE] Failed to initialize notification stream', error);
        if (isMounted) {
        errorCount.current += 1;
          retryTimeout = setTimeout(connectSSE, 10000);
        }
      }
    };

    connectSSE();
    return () => { 
      isMounted = false;
      abortController.abort(); 
      clearTimeout(retryTimeout); 
    };
  }, [queryClient, isSignedIn, getToken]);

  const handleNotificationClick = (notification: Notification) => {
    // If it's unread, fire the mutation in the background
    if (!notification.read) {
      markSingleReadMutation.mutate(notification.id);
    }
    
    // If there's a link attached to the notification, route the user
    const targetLink = notification.link || (notification as any).linkUrl;
    if (targetLink) {
      setIsOpen(false);
      if (targetLink.startsWith('http://') || targetLink.startsWith('https://')) {
        window.open(targetLink, '_blank', 'noopener,noreferrer');
      } else {
        navigate(targetLink);
      }
    }
  };

  const toggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    localStorage.setItem('notifications_muted', String(nextMuted));
  };

  const notifications: Notification[] = notificationsPage?.content || [];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "relative p-2 text-gray-500 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-primary dark:bg-slate-800",
          className
        )}
        aria-label="View notifications"
      >
        <Bell size={20} />
        
        {/* Red Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 border-2 border-white rounded-full transform translate-x-1/4 -translate-y-1/4">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col max-h-[85vh] origin-top-right animate-in fade-in scale-95 duration-200 dark:bg-slate-900">
          
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/50 dark:bg-slate-950">
            <h3 className="font-bold text-gray-900 text-base dark:text-white">Notifications</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevents clicks from misfiring dropdown toggles
                  toggleMute();
                }}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                title={isMuted ? "Unmute sounds" : "Mute sounds"}
              >
                {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <Link
                to="/profile/notifications"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
                title="Notification Settings"
              >
                <Settings className="w-4 h-4" />
              </Link>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending}
                  className="flex items-center gap-1.5 text-xs font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors disabled:opacity-50"
                >
                  <CheckCheck className="w-4 h-4" />
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto overscroll-contain flex-1">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-8 h-8 animate-spin mb-3 text-gray-300" />
                <p className="text-sm font-medium">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 dark:bg-slate-950">
                  <Inbox className="w-8 h-8 text-gray-300" />
                </div>
                <h4 className="text-gray-900 font-semibold mb-1 dark:text-white">You're all caught up!</h4>
                <p className="text-sm text-gray-500">There are no new notifications to review.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-gray-800">
                {notifications.map((notification) => {
                  const targetLink = notification.link || (notification as any).linkUrl;
                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`group flex items-start gap-4 p-4 transition-colors cursor-pointer ${
                        notification.read 
                          ? 'bg-white hover:bg-gray-50 dark:bg-slate-900 dark:hover:bg-slate-800/50' 
                          : 'bg-blue-50/40 hover:bg-blue-50/80 dark:bg-slate-800/60 dark:hover:bg-slate-800'
                      }`}
                    >
                      {/* Unread dot indicator */}
                      <div className="pt-1.5 w-2 flex-shrink-0 flex justify-center">
                        {!notification.read && <div className="w-2 h-2 rounded-full bg-blue-600 shadow-sm" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`text-sm mb-0.5 ${notification.read ? 'text-gray-900 font-medium dark:text-gray-200' : 'text-gray-900 font-bold dark:text-white'}`}>
                          {notification.title}
                        </p>
                        <p className={`text-sm line-clamp-2 ${notification.read ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          {notification.message}
                        </p>
                        {targetLink && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#0044FF] dark:text-blue-400 group-hover:underline">
                            <ExternalLink size={12} className="shrink-0" />
                            <span className="truncate max-w-[240px]">
                              {targetLink.replace(/^https?:\/\//, '')}
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-2 font-medium">
                          {/* A lightweight native way to format dates */}
                          {new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }).format(new Date(notification.createdAt))}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};